import {partition} from '@benchling/js-utils';
import * as d3 from 'd3';
import React from 'react';

import DisplayInfoLabel from 'molbio/components/visualizations/DisplayInfoLabel';
import {
  PolymerPreviewOverlay,
  VisualPolymer,
  VisualPolymerFeature,
  VisualPolymerFragment,
} from 'molbio/components/visualizations/PolymerPreviewConstants';
import {getEditOverlayRenderParams} from 'molbio/components/visualizations/VisualizationUtil';
import {
  axisSpacing,
  getCirclePoint,
  getTextAnchorForCircularText,
  getArcForStrand,
} from 'molbio/maps/circular/CircularUtil';
import {formatAnnotationDisplayName} from 'molbio/maps/linear/AnnotationsHelpers';
import * as sequtil from 'molbio/SeqUtil';
import {getCircularHomologyStripeData} from 'molbio/visualization/FragmentVisualizationUtil';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import {GRAY_LIGHT, NEAR_BLACK} from 'ui-library/Colors';
import Arc from 'ui-library/svg/Arc';
import ArcText from 'ui-library/svg/ArcText';
import Line from 'ui-library/svg/Line';
import {getAngle, getAngles, polarToCartesian, toStandardAngle} from 'util/geometry/GeometryUtil';

const WIDTH = 300;
const RADIUS = 120;
const CENTER = {x: WIDTH / 2, y: WIDTH / 2};
const AXIS_THICKNESS = 15;
const AXIS_NUMBER_TICK_LENGTH = 4;
const AXIS_INNER_RADIUS = RADIUS - AXIS_THICKNESS / 2;
const AXIS_NUMBER_LABEL_RADIUS = AXIS_INNER_RADIUS - AXIS_NUMBER_TICK_LENGTH - 6;
const ANNOTATION_OUTER_RADIUS = AXIS_NUMBER_LABEL_RADIUS - 16;
const ANNOTATION_THICKNESS = 12;
const OUTER_EDIT_OVERLAY_RADIUS = RADIUS + AXIS_THICKNESS + 4;
const EDIT_OVERLAY_THICKNESS = AXIS_THICKNESS * 2 + 4;

/**
 * Renders approximately 6 axis numbering labels and tick marks on a circular preview.
 */
function AxisNumbering({
  length,
  startIndex,
  idPrefix,
}: {
  length: number;
  startIndex: number;
  idPrefix: string;
}): JSX.Element {
  const incr = axisSpacing(length, 6);
  const labels = d3.range(incr, length + 1, incr);
  return (
    <>
      {labels.map((label) => {
        const angle = getAngle(sequtil.displayToModel(label, startIndex), length);
        const tickStartPoint = polarToCartesian(CENTER, AXIS_INNER_RADIUS, angle);
        const tickEndPoint = polarToCartesian(CENTER, AXIS_INNER_RADIUS - AXIS_NUMBER_TICK_LENGTH, angle);
        const labelText = String(label);
        const anglePadding = (3 * labelText.length) / AXIS_NUMBER_LABEL_RADIUS;
        return (
          <g key={`axis-label-${labelText}`}>
            <Line startPoint={tickStartPoint} endPoint={tickEndPoint} stroke={NEAR_BLACK} strokeWidth={2} />
            <ArcText
              fontWeight={700}
              fontSize={10}
              id={`${idPrefix}-axis-label-${labelText}`}
              text={labelText}
              circleCenter={CENTER}
              radius={AXIS_NUMBER_LABEL_RADIUS}
              startAngle={angle - anglePadding}
              endAngle={angle + anglePadding}
            />
          </g>
        );
      })}
    </>
  );
}

/**
 * Renders an annotation on a circular preview at the given layer.
 */
function Annotation({
  annotation,
  layer,
  seqLength,
  id,
}: {
  annotation: VisualPolymerFeature;
  layer: number;
  seqLength: number;
  id: string;
}): JSX.Element {
  const {start, end, name, color, strand} = annotation;
  const {startAngle, endAngle} = getAngles(start, end, seqLength, (bp: number) => getAngle(bp, seqLength));
  const radius = ANNOTATION_OUTER_RADIUS - (ANNOTATION_THICKNESS + 4) * layer;
  const availableSpace = toStandardAngle(endAngle - startAngle) * radius - 16;
  const displayText = formatAnnotationDisplayName(name, availableSpace / 5);
  const arcPathFxn = getArcForStrand(strand)
    .innerRadius(radius - ANNOTATION_THICKNESS / 2)
    .outerRadius(radius + ANNOTATION_THICKNESS / 2);

  return (
    <g>
      <path
        transform={`translate(${CENTER.x}, ${CENTER.y})`}
        d={arcPathFxn({radius, startAngle, endAngle})!}
        fill={color}
        fillOpacity={0.7}
        stroke={d3.rgb(color).darker().formatHex()}
      />
      {displayText.length > 0 && (
        <ArcText
          fontWeight={500}
          fontSize={10}
          id={id}
          text={displayText}
          circleCenter={CENTER}
          radius={radius}
          startAngle={startAngle}
          endAngle={endAngle}
        />
      )}
    </g>
  );
}

/**
 * Renders visuals representing edit regions on a circular preview.
 * Delete visuals show the solid fragment outside of the axis, and only if the region is longer than
 * 5% of the total length. Other visuals show the solid fragment on the axis.
 */
function EditOverlay({overlay, seqLength}: {overlay: PolymerPreviewOverlay; seqLength: number}): JSX.Element {
  const {start, end} = overlay;
  const {showFragmentOutside, fragmentColor, boundaryLineColor, backgroundColor} = getEditOverlayRenderParams(
    overlay,
    seqLength
  );

  const lineOuterRadius = AXIS_INNER_RADIUS + EDIT_OVERLAY_THICKNESS + 2;
  const {startAngle, endAngle} = getAngles(start, end, seqLength, (bp: number) => getAngle(bp, seqLength));
  const angleOffset = (2 * Math.PI) / 180;
  const solidFragmentStartAngle = showFragmentOutside
    ? toStandardAngle(startAngle + angleOffset)
    : startAngle;
  const solidFragmentEndAngle = showFragmentOutside ? toStandardAngle(endAngle - angleOffset) : endAngle;

  return (
    <g>
      {backgroundColor && (
        <Arc
          circleCenter={CENTER}
          radius={AXIS_INNER_RADIUS + EDIT_OVERLAY_THICKNESS / 2}
          startAngle={startAngle}
          endAngle={endAngle}
          pathProps={{stroke: backgroundColor, strokeWidth: EDIT_OVERLAY_THICKNESS}}
        />
      )}
      {fragmentColor && (
        <Arc
          circleCenter={CENTER}
          radius={showFragmentOutside ? OUTER_EDIT_OVERLAY_RADIUS : RADIUS}
          startAngle={solidFragmentStartAngle}
          endAngle={solidFragmentEndAngle}
          pathProps={{stroke: fragmentColor, strokeWidth: AXIS_THICKNESS}}
        />
      )}
      {boundaryLineColor && (
        <>
          <Line
            startPoint={polarToCartesian(CENTER, AXIS_INNER_RADIUS, startAngle)}
            endPoint={polarToCartesian(CENTER, lineOuterRadius, startAngle)}
            stroke={boundaryLineColor}
            strokeWidth={3}
            strokeLinecap="round"
          />
          <Line
            startPoint={polarToCartesian(CENTER, AXIS_INNER_RADIUS, endAngle)}
            endPoint={polarToCartesian(CENTER, lineOuterRadius, endAngle)}
            stroke={boundaryLineColor}
            strokeWidth={3}
            strokeLinecap="round"
          />
        </>
      )}
    </g>
  );
}

function _renderFragments(fragments: Array<VisualPolymerFragment>, length: number): JSX.Element {
  const [nonHomologyFragments, homologyFragments] = partition(
    fragments,
    (fragment) => fragment.homologyRegionOtherColor == null
  );
  return (
    <>
      {nonHomologyFragments.map(({start, end, color}) => {
        const startAngle = getAngle(start, length);
        const endAngle = getAngle(end, length);
        return (
          <Arc
            key={`fragment-${start}-${end}`}
            circleCenter={CENTER}
            radius={RADIUS}
            startAngle={startAngle}
            endAngle={endAngle}
            pathProps={{stroke: color, strokeWidth: AXIS_THICKNESS}}
          />
        );
      })}
      {homologyFragments.flatMap(
        ({
          start,
          end,
          color,
          homologyRegionOtherColor,
        }: {
          start: number;
          end: number;
          color: string;
          homologyRegionOtherColor: string;
        }) => {
          const homologyStripeData = getCircularHomologyStripeData(
            {start, end, color, homologyRegionOtherColor},
            length,
            RADIUS,
            (basePair: number) => getAngle(basePair, length)
          );
          return homologyStripeData.map(({pieceStartAngle, pieceEndAngle, color}, i) => (
            <Arc
              key={`fragment-${start}-${end}-${i}`}
              circleCenter={CENTER}
              radius={RADIUS}
              startAngle={pieceStartAngle}
              endAngle={pieceEndAngle}
              pathProps={{stroke: color, strokeWidth: AXIS_THICKNESS}}
            />
          ));
        }
      )}
    </>
  );
}

export interface CircularPolymerPreviewProps {
  polymer: VisualPolymer;
  annotationLayers: Array<Array<VisualPolymerFeature>>;
  // Drawing arced text relies on having unique IDs, including across multiple SVGs on the page
  uniqueIdPrefix: string;
}

/**
 * A static, non-interactive preview of a circular polymer.
 */
export default function CircularPolymerPreview({
  polymer,
  annotationLayers,
  uniqueIdPrefix,
}: CircularPolymerPreviewProps): JSX.Element {
  const {fragments, length, startIndex, cutSites, overlays} = polymer;

  return (
    <svg viewBox={`0 0 ${WIDTH} ${WIDTH}`} width="100%" fontFamily={FONT_FAMILY_MONOSPACED}>
      <Arc // Arc for the whole "base" sequence
        circleCenter={CENTER}
        radius={RADIUS}
        startAngle={0}
        endAngle={0}
        pathProps={{stroke: GRAY_LIGHT, strokeWidth: AXIS_THICKNESS}}
      />
      {length > 0 && <AxisNumbering length={length} startIndex={startIndex} idPrefix={uniqueIdPrefix} />}
      {_renderFragments(fragments, length)}
      {overlays.map((overlay) => (
        <EditOverlay key={`edit-${overlay.start}-${overlay.end}`} overlay={overlay} seqLength={length} />
      ))}
      {cutSites.map(({enzymeId, position}) => {
        const sequencePoint = getCirclePoint(CENTER, position, length, AXIS_INNER_RADIUS);
        const textPoint = getCirclePoint(CENTER, position, length, RADIUS + 16);
        const textPointInfo = {point: textPoint, anchor: getTextAnchorForCircularText(CENTER, textPoint)};
        return (
          <DisplayInfoLabel
            key={`cutsite-${position}`}
            sequencePoint={sequencePoint}
            textPointInfo={textPointInfo}
            displayInfo={{enzymeId, bases: null}}
          />
        );
      })}
      <g>
        {annotationLayers.map((layer, layerIdx) =>
          layer.map((annotation, idx) => {
            const key = `annotation-${layerIdx}-${idx}`;
            return (
              <Annotation
                key={key}
                id={`${uniqueIdPrefix}-${key}`}
                annotation={annotation}
                layer={layerIdx}
                seqLength={length}
              />
            );
          })
        )}
      </g>
    </svg>
  );
}
